import {
  Button,
  ButtonGroup,
  Chip,
  cn,
  Link,
  ScrollShadow,
  Tooltip,
} from '@heroui/react'
import { useStatsigClient } from '@statsig/react-bindings'
import { getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table'
import DownloadButton from 'components/Dashboard/CallLogs/DownloadButton'
import PlayButton from 'components/Dashboard/CallLogs/PlayButton'
import CopyButton from 'components/Hero/CopyButton'
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js'
import { AlertCircle, ChevronRight, FileText, MoveDownRight, MoveUpRight } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useLocalStorage } from 'usehooks-ts'
import { convertToMinutesAndSeconds } from 'utils/formatting/secsToMins'
import { toDtmy } from 'utils/formatting/toDtmy'
import { parsePathwayTags } from 'utils/funcs/call-logs/parsePathwayTags'

const colorMap = {
  'completed': 'success',
  'no-answer': 'default',
  'busy': 'secondary',
  'failed': 'danger',
  'canceled': 'warning',
}

const colorMapWarmTransfer = {
  WAITING: 'default',
  ATTEMPTING: 'default',
  QUEUED: 'default',
  STARTED: 'default',
  MERGED: 'success',
  FAILED: 'danger',
  HUNG_UP: 'warning',
  CANCELLED: 'warning',
  TIMED_OUT: 'danger',
  NO_ANSWER: 'default',
}

function renderPhoneNumber(value) {
  if (!value || typeof value !== 'string')
    return value ?? '-'
  // If it's a web client uuid
  if (value.length === 36) {
    return (
      <Chip size="sm" color="primary" variant="flat" radius="sm">
        Web Client
      </Chip>
    )
  }
  // If it's a phone number
  if (isValidPhoneNumber(value)) {
    const parsedNumber = parsePhoneNumberFromString(value)
    if (parsedNumber) {
      return parsedNumber.formatNational()
    }
  }
  // If it's an inbound label
  if (value?.length) {
    return (
      <Chip size="sm" color="default" variant="flat" radius="sm">
        {value}
      </Chip>
    )
  }
  return '-'
}

export default function useCallLogsTable({ totalCount, data = [], include = null, onRowClick = null }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [, setActiveTab] = useLocalStorage('activeTab', 'transcript')
  const location = useLocation()
  const navigate = useNavigate()
  const { client: statsigClient } = useStatsigClient()

  const { callId } = useParams()
  const rowSelection = useMemo(() => callId ? { [callId]: true } : {}, [callId])

  const sortBy = searchParams.get('sortBy') || 'created_at'
  const sortDir = searchParams.get('sortDir') || 'desc'
  const sorting = useMemo(() => [{ id: sortBy, desc: sortDir === 'desc' }], [sortBy, sortDir])

  const hidePathways = statsigClient.checkGate('hide_pathway_data')

  const handleSortChange = (updater) => {
    const newSorting = updater(sorting)
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0]
      searchParams.set('sortBy', id)
      searchParams.set('sortDir', desc ? 'desc' : 'asc')
    }
    else {
      searchParams.delete('sortBy')
      searchParams.delete('sortDir')
    }
    setSearchParams(searchParams)
  }

  const handleRowSelectionChange = useCallback((updater) => {
    const queryString = searchParams.toString()
    let c_id = updater

    if (typeof updater === 'function') {
      const newSelection = updater()
      c_id = Object.keys(newSelection)[0]
    }

    if (onRowClick) {
      onRowClick(c_id)
      return
    }

    const route = location.pathname.split('/').slice(0, 3).join('/')

    navigate(`${route}/${c_id || ''}${queryString ? `?${queryString}` : ''}`)
  }, [searchParams])

  const handleViewNotes = useCallback((c_id) => {
    setActiveTab('notes')
    handleRowSelectionChange(c_id)
  }, [searchParams])

  const columns = useMemo(() => [
    {
      id: 'icon',
      header: `${totalCount} Total`,
      size: 50,
      enableResizing: false,
      enableSorting: false,
      cell: ({ row }) => {
        const handleExpand = (e) => {
          e.stopPropagation()
          e.preventDefault()
          row.getToggleExpandedHandler()()
        }
        return row.original.transferred_calls?.length
          ? (
              <Tooltip content={row.getIsExpanded() ? 'Hide Transferred Call(s)' : 'Show Transferred Call(s)'}>
                <ChevronRight
                  size={20}
                  color="black"
                  onClick={handleExpand}
                  className={cn('mx-auto transition-transform', row.getIsExpanded()
                    ? `rotate-90`
                    : `animate-pulse`)}
                />
              </Tooltip>
            )
          : row.original.inbound
            ? <MoveDownRight size={15} color="#4CAF50" className="mx-auto" />
            : <MoveUpRight size={15} color="#2196F3" className="mx-auto" />
      },
    },
    {
      accessorKey: 'to_label',
      header: 'To',
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue()
        return renderPhoneNumber(value)
      },
    },
    {
      accessorKey: 'from_label',
      header: 'From',
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue()
        return renderPhoneNumber(value)
      },
    },
    {
      accessorKey: 'call_length',
      header: 'Duration',
      size: 80,
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue()
        return value ? convertToMinutesAndSeconds(value) : '-'
      },
    },
    {
      accessorKey: 'recording_url',
      header: 'Recording',
      enableSorting: false,
      size: 80,
      cell: ({ getValue, row }) => {
        const url = getValue()
        return url
          ? (
              <ButtonGroup>
                <PlayButton handleSelect={() => handleRowSelectionChange(row.original.c_id)} />
                <DownloadButton c_id={row.original.c_id} />
              </ButtonGroup>
            )
          : '-'
      },
    },
    {
      accessorKey: 'created_at',
      enableSorting: true,
      header: 'Created',
      cell: ({ getValue }) => {
        const date = getValue()
        return date ? toDtmy(new Date(date)) : '-'
      },
    },
    {
      accessorKey: 'c_id',
      header: 'Call ID',
      size: 100,
      enableSorting: false,
      cell: ({ getValue }) => {
        const callId = getValue()
        // this means that the call hasn't started and that it's queued (for proxy agents.)
        if (callId?.startsWith('temp_'))
          return '-'
        return (
          <CopyButton value={callId} displayText={`${callId?.substring(0, 6)}...`} />
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ getValue }) => {
        const status = getValue()
        return (
          <Chip
            variant="dot"
            radius="sm"
            size="sm"
            color={colorMap[status] ?? 'default'}
            className={`
              text-${colorMap[status]}
              uppercase
            `}
          >
            {status ?? 'unknown'}
          </Chip>
        )
      },
    },
    {
      accessorKey: 'pathway_id',
      header: 'Pathway',
      enableSorting: false,
      cell: ({ getValue, row }) => {
        const pathwayId = getValue()
        return pathwayId
          ? (
              <Link
                href={`/dashboard/convo-pathways?id=${pathwayId}`}
                size="sm"
                underline="always"
                className="text-tiny"
                isDisabled={hidePathways}
              >
                {row.original.pathway_name ?? 'Untitled Pathway'}
              </Link>
            )
          : '-'
      },
    },
    {
      accessorKey: 'pathway_tags',
      header: 'Tags',
      enableSorting: false,
      cell: ({ getValue }) => {
        const tags = getValue()
        const parsedTags = parsePathwayTags(tags)
        if (!parsedTags || !parsedTags?.length)
          return '-'

        return (
          <ScrollShadow
            orientation="horizontal"
            className="flex max-w-60 gap-1 px-2"
            hideScrollBar
          >
            {parsedTags?.map(tag => (
              <Chip size="sm" color="default" variant="bordered" radius="sm" key={tag.name ?? tag}>
                <div className="flex items-center gap-1">
                  <div className="size-2 rounded-full" style={{ backgroundColor: tag.color ?? 'gray' }} />
                  {tag.name ?? tag}
                </div>
              </Chip>
            ))}
          </ScrollShadow>
        )
      },
    },
    {
      accessorKey: 'notes',
      header: 'Notes',
      enableSorting: false,
      cell: ({ getValue, row }) => {
        const value = getValue()
        if (!value?.length)
          return '-'
        return (
          <Tooltip content="View Notes">
            <Button onPress={() => handleViewNotes(row.original.c_id)} variant="flat" color="success" size="sm" isIconOnly>
              <FileText size={16} />
            </Button>
          </Tooltip>
        )
      },
    },
    {
      accessorKey: 'transferred_to',
      header: 'Transferred To',
      enableSorting: false,
      cell: ({ row, getValue }) => {
        // if this is a normal call, where there are no warm transfers
        const isWarmTransferCall = row.original.transferred_calls?.length
        const isProxyAgentCall = row.original.is_proxy_agent_call
        const isUnfinishedProxyAgentCall = row.original.c_id.startsWith('temp_')

        if (!isWarmTransferCall && !isProxyAgentCall && !isUnfinishedProxyAgentCall) {
          const value = getValue()
          return renderPhoneNumber(value)
        }

        if (isWarmTransferCall) {
          const value = getValue()
          if (value && value !== 'HOLD MUSIC') {
            return renderPhoneNumber(value)
          }
          const state = row.original.warm_transfer_call?.state?.split('_').join(' ')
          return (
            <Tooltip content={state}>
              <Chip size="sm" color={colorMapWarmTransfer[state] ?? 'default'} variant="dot" radius="sm">
                {state}
              </Chip>
            </Tooltip>
          )
        }

        if (isUnfinishedProxyAgentCall) {
          return (
            <Chip size="sm" color={colorMapWarmTransfer.WAITING} variant="dot" radius="sm">
              WAITING
            </Chip>
          )
        }

        const call_status = row.original.state?.split('_').join(' ')
        return (
          <Chip size="sm" color={colorMapWarmTransfer[call_status] ?? 'default'} variant="dot" radius="sm">
            {call_status}
          </Chip>
        )
      },
    },
    {
      accessorKey: 'batch_id',
      header: 'Batch ID',
      enableSorting: false,
      cell: ({ getValue }) => {
        const batchId = getValue()
        if (!batchId)
          return '-'
        return (
          <CopyButton value={batchId} displayText={`${batchId.substring(0, 6)}...`} />
        )
      },
    },
    {
      accessorKey: 'error_message',
      header: 'Error',
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue()
        return value
          ? (
              <Chip
                size="sm"
                startContent={<AlertCircle size={12} />}
                color="danger"
                radius="sm"
                className="mx-1"
                classNames={{
                  content: 'max-w-64 truncate',
                }}
              >
                {value}
              </Chip>
            )
          : '-'
      },
    },
  ], [totalCount])

  const filteredColumns = useMemo(() => {
    if (include) {
      return columns.filter(column => column.id === 'icon' || include.includes(column.accessorKey))
    }
    return columns
  }, [include, columns])

  return useReactTable({
    data,
    columns: filteredColumns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      rowSelection,
    },
    // EXPANSION
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: row => !!row.original.transferred_calls,
    getSubRows: row => row.transferred_calls?.length ? row.transferred_calls : [],
    // RESIZING
    columnResizeMode: 'onChange',
    // SORTING
    manualSorting: true,
    sortDescFirst: true,
    onSortingChange: handleSortChange,
    // SELECTION
    getRowId: row => row.c_id,
    enableMultiRowSelection: false,
    enableSubRowSelection: false,
    onRowSelectionChange: handleRowSelectionChange,
  })
}
